// src/components/Menu.jsx - Mobile-friendly version with setup wizard restrictions
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { auth, db } from "../utils/firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import logo from '../assets/images/logo2.PNG';
import logogif from '../assets/images/logogif3.gif';
import { AdminOnly, useUserRole } from '../contexts/UserContext';
import WeatherAlerts from './WeatherAlerts';
import { useEventTracker, trackAction, ACTIONS } from '../utils/useEventTracker';
import PremiumFeatureModal from './PremiumFeatureModal'; // ← ADDED FOR FIXTURE WIZARD PAYWALL
import TrialExpiredModal from './TrialExpiredModal'; // ← ADDED FOR TRIAL BLOCKING
import { consoleCapture } from '../utils/consoleCapture'; // ← ADDED FOR FEEDBACK DIAGNOSTICS
import GlobalTicker from './GlobalTicker'; // ← ADDED FOR OWNER BROADCAST MESSAGES

function Menu() {
  const navigate = useNavigate();
  
  // Initialize behavior analytics tracking
  useEventTracker('Menu');
  
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [clubName, setClubName] = useState('');
  const [clubId, setClubId] = useState(null);
  const [clubData, setClubData] = useState(null);
  const [branding, setBranding] = useState(null);
  const [hasCompletedSetup, setHasCompletedSetup] = useState(false);
  const [hasInProgressSetup, setHasInProgressSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Device detection state
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Setup wizard restriction modal
  const [showSetupRestrictionModal, setShowSetupRestrictionModal] = useState(false);

  // ← ADDED: Fixture Wizard Paywall State
  const [showFixtureWizardPaywall, setShowFixtureWizardPaywall] = useState(false);
  
  // ← ADDED: Trial Expired Modal State
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  // Feedback modal states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    from: '',
    topic: '',
    content: '',
    url: ''
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Feedback topic options
  const feedbackTopics = [
    'Bug Report',
    'Feature Request',
    'User Interface Issue',
    'Performance Issue',
    'Data/Sync Problem',
    'Login/Authentication Issue',
    'Pitch Allocation Issue',
    'Satellite Image Problem',
    'Settings Issue',
    'Setup Wizard Issue',
    'General Feedback',
    'Other'
  ];

  // Device detection effect
  useEffect(() => {
    const checkDevice = () => {
      // Check if it's a mobile/tablet device using media queries and user agent
      const isMobileMediaQuery = window.matchMedia('(max-width: 768px)').matches;
      const isTabletMediaQuery = window.matchMedia('(max-width: 1024px) and (min-width: 769px)').matches;
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/.test(userAgent);
      
      // Consider it mobile if either media query suggests mobile/tablet OR user agent suggests mobile
      setIsMobileDevice(isMobileMediaQuery || isTabletMediaQuery || isMobileUserAgent);
    };

    checkDevice();
    
    // Listen for window resize to update device detection
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          // Force a fresh read from Firestore (no cache)
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          
          console.log('Menu - Loading user data for:', currentUser.uid);
          console.log('Menu - hasCompletedSetup:', userDoc.data()?.hasCompletedSetup);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || 'user');
            
            // Check setup completion status
            setHasCompletedSetup(userData.hasCompletedSetup || false);
            
            // Check if user has started but not completed setup
            if (userData.setupProgress && !userData.hasCompletedSetup) {
              const hasAnyProgress = Object.values(userData.setupProgress).some(completed => completed === true);
              setHasInProgressSetup(hasAnyProgress);
            }
            
            if (userData.clubId) {
              setClubId(userData.clubId);
              const clubDoc = await getDoc(doc(db, 'clubs', userData.clubId));
              if (clubDoc.exists()) {
                const clubDataFromDb = clubDoc.data();
                setClubData(clubDataFromDb);
                setClubName(clubDataFromDb.name || clubDataFromDb.Name || clubDataFromDb.clubName || `Club ${userData.clubId}`);
                
                // Load branding from settings document
                const settingsRef = doc(db, 'clubs', userData.clubId, 'settings', 'general');
                const settingsDoc = await getDoc(settingsRef);
                if (settingsDoc.exists() && settingsDoc.data().branding) {
                  console.log('🎨 Menu - Loading branding:', settingsDoc.data().branding);
                  setBranding(settingsDoc.data().branding);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Helper to check if trial is expired
  const isTrialExpired = () => {
    if (!clubData) return false;
    
    const now = new Date();
    const trialEndsAt = clubData.trialEndsAt?.toDate ? 
      clubData.trialEndsAt.toDate() : 
      new Date(clubData.trialEndsAt);
    
    const subscriptionStatus = clubData.subscriptionStatus || 'trial';
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    return subscriptionStatus === 'trial' && daysRemaining <= 0;
  };

  const blockIfTrialExpired = (actionName) => {
    if (isTrialExpired()) {
      console.log(`🚫 Trial expired - blocking ${actionName}`);
      setShowTrialExpiredModal(true);
      return true;
    }
    return false;
  };

  const handleLogout = async () => {
    try {
      // Track logout for behavior analytics
      trackAction(ACTIONS.LOGOUT, { clubId });
      
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Function to navigate to the first pitch
  const handleGetStarted = () => {
    // Navigate to pitch 1 by default, or you can modify this logic
    navigate('/allocator/1');
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  // Function to determine if setup wizard button should be shown
  const shouldShowSetupWizard = () => {
    // Never show for completed setup
    if (hasCompletedSetup) return false;
    
    // Never show for non-admin users
    if (userRole !== 'admin') return false;
    
    // Never show for mobile/tablet users (regardless of role)
    if (isMobileDevice) return false;
    
    // Show for admin users on desktop/PC
    return true;
  };

  // Function to handle setup wizard click with restrictions
  const handleSetupWizard = () => {
    // If user is admin but on mobile/tablet, show restriction modal
    if (userRole === 'admin' && isMobileDevice) {
      setShowSetupRestrictionModal(true);
      setIsMobileMenuOpen(false); // Close mobile menu
      return;
    }
    
    // If user is not admin, don't allow access
    if (userRole !== 'admin') {
      console.warn('Setup wizard access denied: User is not admin');
      return;
    }
    
    // If user is admin on desktop/PC, proceed normally
    navigate('/setup-wizard');
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  // ← ADDED: Function to handle Fixture Wizard navigation with paywall check
  const handleFixtureWizardClick = () => {
    const currentTier = clubData?.subscriptionTier || 'basic';
    
    // Only Premium users can access Fixture Wizard
    if (currentTier !== 'premium') {
      setShowFixtureWizardPaywall(true);
      setIsMobileMenuOpen(false); // Close mobile menu
      return;
    }
    
    // Premium users can proceed
    navigate('/fixture-scheduler');
    setIsMobileMenuOpen(false);
  };

  // Handle feedback modal
  const openFeedbackModal = () => {
    setFeedbackForm({
      from: user?.email || '',
      topic: '',
      content: '',
      url: window.location.href
    });
    setShowFeedbackModal(true);
    setIsMobileMenuOpen(false); // Close mobile menu when opening modal
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackForm({
      from: '',
      topic: '',
      content: '',
      url: ''
    });
  };

  const handleFeedbackInputChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitFeedback = async () => {
    if (!feedbackForm.from || !feedbackForm.topic || !feedbackForm.content) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      // Debug logging
      console.log('Submitting feedback:', {
        email: feedbackForm.from,
        club: clubName,
        topic: feedbackForm.topic,
        message: feedbackForm.content
      });

      // =================================================================
      // STEP 1: Save to Firestore with full diagnostics (silent)
      // =================================================================
      try {
        await addDoc(collection(db, 'feedback'), {
          // User-provided fields
          from: feedbackForm.from,
          topic: feedbackForm.topic,
          content: feedbackForm.content,
          url: feedbackForm.url,
          
          // Context fields
          clubId: clubId || null,
          clubName: clubName || null,
          userId: user?.uid || null,
          userRole: userRole || null,
          
          // Timestamp
          submittedAt: serverTimestamp(),
          
          // =============================================================
          // SILENT DIAGNOSTICS - User never sees this
          // =============================================================
          diagnostics: {
            consoleLogs: consoleCapture.getLogs(),
            logCount: consoleCapture.getLogs().length,
            capturedAt: new Date().toISOString(),
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
            userAgent: navigator.userAgent,
            referrer: document.referrer || null,
            isMobile: isMobileDevice
          }
        });
        console.log('✅ Feedback saved to Firestore with diagnostics');
      } catch (firestoreError) {
        // Don't block submission if Firestore fails - just log it
        console.error('⚠️ Firestore save failed (continuing with Netlify):', firestoreError);
      }

      // =================================================================
      // STEP 2: Also send via Netlify Forms for email notification
      // =================================================================
      const formData = new FormData();
      formData.append('form-name', 'feedback-form');
      formData.append('email', feedbackForm.from);
      formData.append('club', clubName);
      formData.append('topic', feedbackForm.topic);
      formData.append('message', feedbackForm.content);
      formData.append('url', feedbackForm.url);
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString()
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        alert('✅ Thank you for your feedback! We\'ll review it shortly.');
        closeFeedbackModal();
        return;
      }
      
      throw new Error('Form submission failed');
    } catch (error) {
      console.error('Feedback submission error:', error);
      
      // Fallback: mailto method
      const subject = `Pitchallo Feedback: ${feedbackForm.topic}`;
      const body = `From: ${feedbackForm.from}
Club: ${clubName}
Topic: ${feedbackForm.topic}
URL: ${feedbackForm.url}
Message:
${feedbackForm.content}

---
Sent from Pitchallo Feedback Form`;

      const mailtoLink = `mailto:giannini000@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      try {
        window.location.href = mailtoLink;
        setTimeout(() => {
          alert('Your email client should have opened.');
        }, 1000);
        closeFeedbackModal();
      } catch (mailtoError) {
        console.error('Mailto also failed:', mailtoError);
        closeFeedbackModal();
      }
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Navigation button handler with mobile menu close
  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  // Handler for tier changes
  const handleTierChange = async (newTier) => {
    console.log('Tier changed to:', newTier);
    if (clubId) {
      try {
        const clubDoc = await getDoc(doc(db, 'clubs', clubId));
        if (clubDoc.exists()) {
          setClubData(clubDoc.data());
        }
      } catch (error) {
        console.error('Error reloading club data:', error);
      }
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#243665'
      }}>
        <div style={{ color: 'white', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#243665',
      fontFamily: 'system-ui, sans-serif',
      position: 'relative'
    }}>
      {/* Mobile Header */}
      <div className="mobile-header" style={{
        display: 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        backgroundColor: '#243665',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        zIndex: 1001
      }}>
        <img 
          src={logo}
          alt="Pitch Allocator"
          style={{
            height: '40px',
            width: 'auto',
            objectFit: 'contain'
          }}
        />
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '8px'
          }}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Desktop/Tablet Layout */}
      <div className="desktop-layout" style={{
        display: 'flex',
        gap: '0'
      }}>
        {/* Left Sidebar */}
        <div style={{
          width: '250px',
          flexShrink: 0,
          backgroundColor: '#243665',
          height: '100vh',
          position: 'sticky',
          top: 0,
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid rgba(255,255,255,0.1)'
        }}>
          {/* App Logo Section */}
          <div style={{
            width: '100%',
            padding: '16px',
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img 
              src={logo}
              alt="Pitch Allocator"
              style={{
                width: '100%',
                maxWidth: '218px',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          
          {/* User Info Section - Branded Card */}
          <div style={{
            padding: '12px'
          }}>
            <div style={{
              background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}>
              {/* Club Logo + Name Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '6px'
              }}>
                {branding?.clubLogoUrl && (
                  <img 
                    src={branding.clubLogoUrl} 
                    alt="Club Logo" 
                    style={{
                      height: '58px',
                      width: '58px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: 'white',
                  lineHeight: '1.3'
                }}>
                  {clubName || 'Loading...'}
                </div>
              </div>
              <div style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.7)',
                marginBottom: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.email || 'Not logged in'}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'white',
                padding: '4px 10px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                display: 'inline-block',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
              </div>
            
            {/* Setup status indicator */}
            {!hasCompletedSetup && userRole === 'admin' && (
              <div style={{
                fontSize: '11px',
                color: 'rgba(251, 191, 36, 0.9)',
                padding: '4px 8px',
                backgroundColor: 'rgba(251, 191, 36, 0.2)',
                borderRadius: '4px',
                display: 'inline-block',
                marginTop: '8px',
                marginLeft: '8px'
              }}>
                {hasInProgressSetup ? 'Setup In Progress' : 'Setup Pending'}
              </div>
            )}
            
            {/* Subscription Info */}
            {clubData && (
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255,255,255,0.2)'
              }}>
                {/* Subscription Tier */}
                <div style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.6)',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Subscription
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'white',
                  fontWeight: '600',
                  marginBottom: '6px',
                  textTransform: 'capitalize'
                }}>
                  {clubData.subscriptionTier || 'Free'} Plan
                </div>
                
                {/* Subscription Status */}
                {clubData.subscriptionStatus && (
                  <div style={{
                    fontSize: '12px',
                    color: clubData.subscriptionStatus === 'active' ? '#86efac' : 
                           clubData.subscriptionStatus === 'trial' ? '#fde047' : 
                           '#fca5a5',
                    padding: '3px 8px',
                    backgroundColor: clubData.subscriptionStatus === 'active' ? 'rgba(34, 197, 94, 0.2)' : 
                                     clubData.subscriptionStatus === 'trial' ? 'rgba(234, 179, 8, 0.2)' : 
                                     'rgba(239, 68, 68, 0.2)',
                    borderRadius: '4px',
                    display: 'inline-block',
                    fontWeight: '500'
                  }}>
                    {clubData.subscriptionStatus === 'active' ? '✓ Active' : 
                     clubData.subscriptionStatus === 'trial' ? '⏱ Trial' : 
                     clubData.subscriptionStatus === 'canceled' ? '✕ Canceled' :
                     clubData.subscriptionStatus}
                  </div>
                )}
                
                {/* Trial Days Remaining (if on trial) */}
                {clubData.subscriptionStatus === 'trial' && clubData.trialEndsAt && (() => {
                  const now = new Date();
                  const trialEndsAt = clubData.trialEndsAt?.toDate ? 
                    clubData.trialEndsAt.toDate() : 
                    new Date(clubData.trialEndsAt);
                  const msRemaining = trialEndsAt - now;
                  const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
                  
                  if (daysRemaining <= 0) {
                    return (
                      <div style={{
                        fontSize: '11px',
                        color: '#fca5a5',
                        marginTop: '4px'
                      }}>
                        Trial expired
                      </div>
                    );
                  }
                  
                  return (
                    <div style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.7)',
                      marginTop: '4px'
                    }}>
                      {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
                    </div>
                  );
                })()}
              </div>
            )}
            </div>
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Weather Alerts - Desktop - Above navigation buttons */}
          {clubId && (
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <WeatherAlerts clubId={clubId} />
            </div>
          )}

          {/* Bottom navigation buttons */}
          <div style={{
            padding: '12px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px'
          }}>
            <button
              onClick={() => navigate('/club-pitch-map')}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Club Map
            </button>

            <button
              onClick={() => navigate('/coaches-hub')}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Coaches Hub
            </button>

            <button
              onClick={() => {
                if (blockIfTrialExpired('Reporting')) return;
                navigate('/capacity-outlook');
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Reporting
            </button>

            {/* ← MODIFIED: Fixture Wizard button now checks for Premium tier */}
             <AdminOnly>
            <button
              onClick={handleFixtureWizardClick}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Fixture Wizard
            </button>
            </AdminOnly>
            
            <AdminOnly>
              <button
                onClick={() => navigate('/settings')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }}
              >
                Settings
              </button>
            </AdminOnly>

            <button
              onClick={openFeedbackModal}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Feedback
            </button>

            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{
          flex: 1,
          backgroundColor: '#243665',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px'
        }}>
          {/* Animated GIF Logo */}
          <div style={{
            marginBottom: '48px'
          }}>
            <img 
              src={logogif}
              alt="Pitchallo Animation"
              style={{
                maxWidth: '400px',
                width: '100%',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>

          {/* Welcome Message for New Users */}
          {!hasCompletedSetup && userRole === 'admin' && (
            <div style={{
              textAlign: 'center',
              marginBottom: '32px',
              maxWidth: '600px'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '16px',
                margin: '0 0 16px 0'
              }}>
                Welcome to Pitchallo!
              </h2>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.6,
                margin: 0
              }}>
                {hasInProgressSetup 
                  ? `Let's continue setting up ${clubName}. You can resume where you left off or jump straight into managing pitch allocations.`
                  : `Let's get ${clubName} set up for success. Our quick setup wizard will guide you through creating teams, configuring your pitch layout, and getting everything ready.`
                }
              </p>
            </div>
          )}

          {/* Button Container */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            alignItems: 'center'
          }}>
            {/* Get Started Button - Only show after setup is completed */}
            {hasCompletedSetup && (
              <button
                onClick={() => navigate('/club-pitch-map')}
                style={{
                  padding: '20px 48px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#059669';
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#10b981';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.2)';
                }}
              >
                Get Started
              </button>
            )}

            {/* Setup Wizard Button - Only show for admin users on desktop/PC who haven't completed setup */}
            {shouldShowSetupWizard() && (
              <button
                onClick={handleSetupWizard}
                style={{
                  padding: '16px 40px',
                  backgroundColor: 'rgba(59, 130, 246, 0.9)',
                  color: 'white',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.9)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
              >
                {/* Icon and text */}
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  justifyContent: 'center'
                }}>
                  <span style={{ fontSize: '24px' }}>🧙‍♂️</span>
                  {hasInProgressSetup ? 'Resume Setup Wizard' : 'Start Setup Wizard'}
                </span>
                
                {/* Subtle glow effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 2s infinite',
                  pointerEvents: 'none'
                }} />
              </button>
            )}
          </div>

          {/* Helpful tip for new users */}
          {!hasCompletedSetup && userRole === 'admin' && !isMobileDevice && (
            <div style={{
              marginTop: '40px',
              padding: '20px',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              maxWidth: '500px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                marginBottom: '12px'
              }}>
                💡
              </div>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: 1.5,
                margin: 0
              }}>
                <strong>Pro tip:</strong> The setup wizard takes about 5 minutes and will help you get the most out of Pitchallo. 
                You can always access it later from Settings if you skip it now.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          style={{
            display: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className="mobile-sidebar"
        style={{
          display: 'none',
          position: 'fixed',
          top: '60px',
          left: 0,
          bottom: 0,
          width: '80%',
          maxWidth: '320px',
          backgroundColor: '#243665',
          transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease-in-out',
          overflowY: 'auto',
          zIndex: 1001,
          flexDirection: 'column',
          padding: '16px',
          gap: '8px',
          borderRight: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        {/* User Info at top - Branded Card */}
        <div style={{
          background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
        }}>
          {/* Club Logo + Name Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '6px'
          }}>
            {branding?.clubLogoUrl && (
              <img 
                src={branding.clubLogoUrl} 
                alt="Club Logo" 
                style={{
                  height: '58px',
                  width: '58px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  flexShrink: 0
                }}
              />
            )}
            <div style={{
              fontSize: '17px',
              fontWeight: '600',
              color: 'white',
              lineHeight: '1.3'
            }}>
              {clubName || 'Loading...'}
            </div>
          </div>
          <div style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '12px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {user?.email || 'Not logged in'}
          </div>
          <div style={{
            fontSize: '11px',
            color: 'white',
            padding: '4px 10px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '20px',
            display: 'inline-block',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
          </div>
          
          {/* Subscription Info */}
          {clubData && (
            <div style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid rgba(255,255,255,0.2)'
            }}>
              {/* Subscription Tier */}
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Subscription
              </div>
              <div style={{
                fontSize: '13px',
                color: 'white',
                fontWeight: '600',
                marginBottom: '6px',
                textTransform: 'capitalize'
              }}>
                {clubData.subscriptionTier || 'Free'} Plan
              </div>
              
              {/* Subscription Status */}
              {clubData.subscriptionStatus && (
                <div style={{
                  fontSize: '12px',
                  color: clubData.subscriptionStatus === 'active' ? '#86efac' : 
                         clubData.subscriptionStatus === 'trial' ? '#fde047' : 
                         '#fca5a5',
                  padding: '3px 8px',
                  backgroundColor: clubData.subscriptionStatus === 'active' ? 'rgba(34, 197, 94, 0.2)' : 
                                   clubData.subscriptionStatus === 'trial' ? 'rgba(234, 179, 8, 0.2)' : 
                                   'rgba(239, 68, 68, 0.2)',
                  borderRadius: '4px',
                  display: 'inline-block',
                  fontWeight: '500'
                }}>
                  {clubData.subscriptionStatus === 'active' ? '✓ Active' : 
                   clubData.subscriptionStatus === 'trial' ? '⏱ Trial' : 
                   clubData.subscriptionStatus === 'canceled' ? '✕ Canceled' :
                   clubData.subscriptionStatus}
                </div>
              )}
              
              {/* Trial Days Remaining (if on trial) */}
              {clubData.subscriptionStatus === 'trial' && clubData.trialEndsAt && (() => {
                const now = new Date();
                const trialEndsAt = clubData.trialEndsAt?.toDate ? 
                  clubData.trialEndsAt.toDate() : 
                  new Date(clubData.trialEndsAt);
                const msRemaining = trialEndsAt - now;
                const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
                
                if (daysRemaining <= 0) {
                  return (
                    <div style={{
                      fontSize: '11px',
                      color: '#fca5a5',
                      marginTop: '4px'
                    }}>
                      Trial expired
                    </div>
                  );
                }
                
                return (
                  <div style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.7)',
                    marginTop: '4px'
                  }}>
                    {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Weather Alerts - Mobile */}
        {clubId && (
          <div style={{ marginBottom: '8px' }}>
            <WeatherAlerts clubId={clubId} />
          </div>
        )}

        {/* Main Action Buttons */}
        {/* Get Started Button - Only show after setup is completed */}
        {hasCompletedSetup && (
          <button
            onClick={handleGetStarted}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: '#10b981',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '600',
              textAlign: 'center'
            }}
          >
            Get Started
          </button>
        )}

        {/* Setup Wizard Button - Show for admin users on mobile (will trigger restriction modal) */}
        {!hasCompletedSetup && userRole === 'admin' && (
          <button
            onClick={handleSetupWizard}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: 'rgba(59, 130, 246, 0.9)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '600',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>🧙‍♂️</span>
            {hasInProgressSetup ? 'Resume Setup Wizard' : 'Start Setup Wizard'}
          </button>
        )}

        {/* Divider */}
        <div style={{
          height: '1px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          margin: '8px 0'
        }} />

        {/* Navigation Buttons */}
        <button
          onClick={() => handleNavigation('/club-pitch-map')}
          style={{
            width: '100%',
            padding: '12px 14px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            textAlign: 'left'
          }}
        >
          Pitch Layout
        </button>
        
        {/* ← MODIFIED: Fixture Wizard button for mobile */}
        <button
          onClick={handleFixtureWizardClick}
          style={{
            width: '100%',
            padding: '12px 14px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            textAlign: 'left'
          }}
        >
          Fixture Scheduler
        </button>
        
        <button
          onClick={() => {
            if (blockIfTrialExpired('Reporting')) return;
            handleNavigation('/capacity-outlook');
          }}
          style={{
            width: '100%',
            padding: '12px 14px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            textAlign: 'left'
          }}
        >
          Reporting
        </button>

        <button
          onClick={() => handleNavigation('/settings')}
          style={{
            width: '100%',
            padding: '12px 14px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            textAlign: 'left'
          }}
        >
          Settings
        </button>

        <button
          onClick={openFeedbackModal}
          style={{
            width: '100%',
            padding: '12px 14px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            textAlign: 'left'
          }}
        >
          Feedback
        </button>

        {/* Spacer */}
        <div style={{ flex: 1, minHeight: '20px' }} />

        {/* Logout button */}
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '12px 14px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            textAlign: 'left'
          }}
        >
          Logout
        </button>
      </div>

      {/* Mobile Main Content */}
      <div 
        className="mobile-content"
        style={{
          display: 'none',
          paddingTop: '80px', // Account for fixed header
          minHeight: '100vh',
          padding: '80px 20px 40px 20px',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexDirection: 'column'
        }}
      >
        {/* Animated GIF Logo - smaller on mobile */}
        <div style={{
          marginBottom: '32px'
        }}>
          <img 
            src={logogif}
            alt="Pitchallo Animation"
            style={{
              maxWidth: '280px',
              width: '100%',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Welcome Message for New Users - Only show for admin users */}
        {!hasCompletedSetup && userRole === 'admin' && (
          <div style={{
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '16px',
              margin: '0 0 16px 0'
            }}>
              Welcome to Pitchallo!
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6,
              margin: 0
            }}>
              {hasInProgressSetup 
                ? `Let's continue setting up ${clubName}. You can resume where you left off or jump straight into managing pitch allocations.`
                : `Let's get ${clubName} set up for success. Our quick setup wizard will guide you through creating teams, configuring your pitch layout, and getting everything ready.`
              }
            </p>
          </div>
        )}

        {/* Button Container */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
          width: '100%',
          maxWidth: '320px'
        }}>
          {/* Get Started Button - Mobile optimized */}
          <button
            onClick={() => navigate('/club-pitch-map')}
            style={{
              width: '100%',
              padding: '18px 24px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              minHeight: '56px' // Better touch target
            }}
          >
            {hasCompletedSetup ? 'Get Started' : 'Skip Setup & Get Started'}
          </button>

          {/* Setup Wizard Button - Mobile optimized - Only show for admin users (will trigger restriction modal) */}
          {!hasCompletedSetup && userRole === 'admin' && (
            <button
              onClick={handleSetupWizard}
              style={{
                width: '100%',
                padding: '16px 24px',
                backgroundColor: 'rgba(59, 130, 246, 0.9)',
                color: 'white',
                border: '2px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minHeight: '56px' // Better touch target
              }}
            >
              <span style={{ fontSize: '20px' }}>🧙‍♂️</span>
              {hasInProgressSetup ? 'Resume Setup Wizard' : 'Start Setup Wizard'}
            </button>
          )}
        </div>
      </div>

      {/* Setup Restriction Modal */}
      {showSetupRestrictionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setShowSetupRestrictionModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '24px',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '12px'
              }}>
                💻
              </div>
              <h2 style={{
                color: 'white',
                fontSize: '24px',
                fontWeight: '700',
                margin: 0
              }}>
                Desktop Required
              </h2>
            </div>

            <div style={{ padding: '24px' }}>
              <p style={{
                fontSize: '16px',
                color: '#374151',
                lineHeight: 1.6,
                margin: '0 0 20px'
              }}>
                The Setup Wizard is designed for desktop use and requires a larger screen to work with satellite imagery and pitch configurations effectively.
              </p>
              
              <p style={{
                fontSize: '16px',
                color: '#374151',
                lineHeight: 1.6,
                margin: '0 0 24px'
              }}>
                Please access Pitchallo from a <strong>desktop computer, laptop, or PC</strong> to run the Setup Wizard.
              </p>

              <button
                onClick={() => setShowSetupRestrictionModal(false)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ← ADDED: Fixture Wizard Paywall Modal */}
      <PremiumFeatureModal
        isOpen={showFixtureWizardPaywall}
        onClose={() => setShowFixtureWizardPaywall(false)}
        featureName="Fixture Wizard"
        currentTier={clubData?.subscriptionTier || 'basic'}
        requiredTiers={['premium']}
        benefits={[
          'Automated fixture scheduling with AI optimization',
          'Smart pitch allocation based on team requirements',
          'Conflict detection across all allocations',
          'Export fixtures directly to your pitch allocator',
          'Save hours on fixture planning each season',
          'Perfect for complex multi-team schedules'
        ]}
        upgradeUrl="/pricing"
      />

      {/* Feedback Modal - Improved for mobile */}
      {showFeedbackModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeFeedbackModal();
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Send Feedback or Report a Bug
              </h2>
              <button
                onClick={closeFeedbackModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px', overflowY: 'auto' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  From (Your Email)
                </label>
                <input
                  type="email"
                  value={feedbackForm.from}
                  onChange={(e) => handleFeedbackInputChange('from', e.target.value)}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '12px', // Larger touch target
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px', // Prevent zoom on iOS
                    boxSizing: 'border-box',
                    minHeight: '44px'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Topic
                </label>
                <select
                  value={feedbackForm.topic}
                  onChange={(e) => handleFeedbackInputChange('topic', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px', // Larger touch target
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px', // Prevent zoom on iOS
                    boxSizing: 'border-box',
                    backgroundColor: 'white',
                    minHeight: '44px'
                  }}
                >
                  <option value="">Select a topic...</option>
                  {feedbackTopics.map((topic, index) => (
                    <option key={index} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Page URL
                </label>
                <input
                  type="text"
                  value={feedbackForm.url}
                  onChange={(e) => handleFeedbackInputChange('url', e.target.value)}
                  placeholder="The page where you encountered this issue"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: '#f9fafb',
                    fontFamily: 'monospace',
                    color: '#6b7280'
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#9ca3af',
                  marginTop: '4px',
                  marginBottom: '0'
                }}>
                  Auto-filled with current page URL
                </p>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Message
                </label>
                <textarea
                  value={feedbackForm.content}
                  onChange={(e) => handleFeedbackInputChange('content', e.target.value)}
                  placeholder="Please describe your feedback, bug report, or suggestion in detail..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '12px', // Larger touch target
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px', // Prevent zoom on iOS
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    minHeight: '120px'
                  }}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              padding: '20px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={closeFeedbackModal}
                style={{
                  padding: '12px 20px', // Larger touch target
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#374151',
                  minHeight: '44px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitFeedback}
                disabled={isSubmittingFeedback}
                style={{
                  padding: '12px 20px', // Larger touch target
                  backgroundColor: isSubmittingFeedback ? '#9ca3af' : '#22c55e',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer',
                  color: 'white',
                  fontWeight: '500',
                  opacity: isSubmittingFeedback ? 0.7 : 1,
                  minHeight: '44px'
                }}
              >
                {isSubmittingFeedback ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for responsive design and animations */}
      <style>
        {`
          @keyframes shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }

          /* Prevent white band on smaller screens */
          html, body, #root {
            min-height: 100%;
            margin: 0;
            padding: 0;
            background-color: #243665;
          }

          /* Mobile Styles */
          @media (max-width: 768px) {
            .desktop-layout {
              display: none !important;
            }
            
            .mobile-header {
              display: flex !important;
            }
            
            .mobile-sidebar {
              display: flex !important;
            }
            
            .mobile-content {
              display: flex !important;
            }
            
            .mobile-overlay {
              display: block !important;
            }
          }

          /* Tablet Styles */
          @media (max-width: 1024px) and (min-width: 769px) {
            .desktop-layout .sidebar {
              width: 200px;
            }
          }

          /* Prevent horizontal scroll on mobile */
          @media (max-width: 768px) {
            html, body {
              overflow-x: hidden;
            }
          }
        `}
      </style>
      
      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
      />
      
      {/* Global Ticker - Owner broadcast messages */}
      <GlobalTicker />
    </div>
  );
}

export default Menu;
